import type { ComponentChildren, JSX } from 'preact';
import type { LucideIcon } from 'lucide-preact';
import { ChevronDown, MoreHorizontal, X } from 'lucide-preact';
import { useEffect, useRef } from 'preact/hooks';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';
export type ButtonSize = 'compact' | 'standard' | 'touch';

interface ButtonProps extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: LucideIcon;
  readonly children: ComponentChildren;
}

export function Button({
  variant = 'secondary',
  size = 'standard',
  icon: IconComponent,
  children,
  class: className,
  ...props
}: ButtonProps) {
  return (
    <button
      {...props}
      class={`bc-button bc-button-${variant} bc-button-${size}${className ? ` ${className}` : ''}`}
    >
      {IconComponent && <IconComponent aria-hidden="true" size={17} strokeWidth={2.2} />}
      <span>{children}</span>
    </button>
  );
}

interface IconButtonProps extends JSX.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly size?: ButtonSize;
}

export function IconButton({
  label,
  icon: IconComponent,
  size = 'standard',
  class: className,
  ...props
}: IconButtonProps) {
  return (
    <button
      {...props}
      class={`bc-icon-button bc-button-${size}${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
    >
      <IconComponent aria-hidden="true" size={18} strokeWidth={2.2} />
    </button>
  );
}

export function CloseButton(props: Omit<IconButtonProps, 'icon'>) {
  return <IconButton {...props} icon={X} />;
}

export function OverflowButton({
  label = 'More actions',
  ...props
}: Omit<IconButtonProps, 'icon' | 'label'> & { readonly label?: string }) {
  return <IconButton {...props} label={label} icon={MoreHorizontal} />;
}

export function SplitButton({
  children,
  menuLabel,
  onMenu,
  ...props
}: ButtonProps & { readonly menuLabel: string; readonly onMenu: () => void }) {
  return (
    <span class="bc-split-button">
      <Button {...props}>{children}</Button>
      <IconButton label={menuLabel} icon={ChevronDown} onClick={onMenu} />
    </span>
  );
}

export function StatusPill({
  tone = 'neutral',
  children,
}: {
  readonly tone?: 'neutral' | 'ready' | 'warning' | 'danger' | 'info';
  readonly children: ComponentChildren;
}) {
  return <span class={`bc-status-pill bc-status-${tone}`}>{children}</span>;
}

export function Disclosure({
  summary = 'More Details',
  children,
}: {
  readonly summary?: string;
  readonly children: ComponentChildren;
}) {
  return (
    <details class="bc-disclosure">
      <summary>{summary}</summary>
      <div>{children}</div>
    </details>
  );
}

export function Dialog({
  id,
  title,
  description,
  role = 'dialog',
  closeLabel = 'Close',
  onClose,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly role?: 'dialog' | 'alertdialog';
  readonly closeLabel?: string;
  readonly onClose: () => void;
  readonly children: ComponentChildren;
}) {
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const priorFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const focusable = () => [
      ...(dialog?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ) ?? []),
    ];
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = focusable();
      if (controls.length === 0) {
        event.preventDefault();
        return;
      }
      const first = controls[0];
      const last = controls.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      priorFocus?.focus();
    };
  }, [onClose]);

  return (
    <div
      class="exit-confirm-overlay"
      role="presentation"
      data-testid={`${id}-overlay`}
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        ref={dialogRef}
        class="exit-confirm-dialog"
        role={role}
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-description` : undefined}
      >
        <CloseButton
          class="exit-confirm-close"
          label={closeLabel}
          size="compact"
          type="button"
          onClick={onClose}
        />
        <h2 id={`${id}-title`}>{title}</h2>
        {description && <p id={`${id}-description`}>{description}</p>}
        {children}
      </section>
    </div>
  );
}
